/**
 * Eingebaute Bildschirmtastatur (BT, freigegeben 2026-11-02; Ablaufregeln R1–R6 in
 * `docs/design-entwuerfe/pos-bildschirmtastatur-anforderungen.md`). Reine Logik ohne React:
 * Belegung, Zeichen einfügen bzw. löschen an Cursor und Auswahl, Feldgrenze, Warnzeile.
 */

/** QWERTZ (deutsch): Ziffern mit ß, Buchstaben mit ü ö ä, letzte Reihe mit , . - @. */
export const TASTATUR_REIHEN: readonly (readonly string[])[] = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'ß'],
  ['q', 'w', 'e', 'r', 't', 'z', 'u', 'i', 'o', 'p', 'ü'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä'],
  ['y', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '-', '@'],
];

/** Ab dieser Breite gibt es die eingebaute Tastatur; darunter die System-Tastatur (BT-E4). */
export const TASTATUR_AB_BREITE = 601;

export function tastaturVerfuegbar(breite: number): boolean {
  return breite >= TASTATUR_AB_BREITE;
}

/** Zeichen einer Taste je nach Umschaltung „Groß“ (ß bleibt ß, Satzzeichen bleiben). */
export function zeichenFuerTaste(taste: string, gross: boolean): string {
  return gross && taste !== 'ß' ? taste.toLocaleUpperCase('de-AT') : taste;
}

/** Feldinhalt mit Auswahl (Start = Ende: Cursor). */
export interface Eingabe {
  wert: string;
  start: number;
  ende: number;
}

function grenzen(e: Eingabe): { start: number; ende: number } {
  const n = e.wert.length;
  const a = Math.min(Math.max(e.start, 0), n);
  const b = Math.min(Math.max(e.ende, 0), n);
  return { start: Math.min(a, b), ende: Math.max(a, b) };
}

/**
 * Zeichen an Cursor bzw. statt der Auswahl einfügen. `maxLaenge` (wie `maxLength` am Feld,
 * negativ oder fehlend = ohne Grenze) kürzt das Eingefügte; ist kein Platz mehr, bleibt alles
 * unverändert.
 */
export function zeichenEinfuegen(e: Eingabe, zeichen: string, maxLaenge?: number): Eingabe {
  const { start, ende } = grenzen(e);
  const rest = e.wert.length - (ende - start);
  const frei =
    maxLaenge === undefined || maxLaenge < 0 ? zeichen.length : Math.max(maxLaenge - rest, 0);
  const neu = zeichen.slice(0, frei);
  if (neu === '' && start === ende) {
    return { wert: e.wert, start, ende };
  }
  const wert = e.wert.slice(0, start) + neu + e.wert.slice(ende);
  return { wert, start: start + neu.length, ende: start + neu.length };
}

/** Rücktaste „← Zeichen“: löscht die Auswahl bzw. das Zeichen vor dem Cursor. */
export function zeichenLoeschen(e: Eingabe): Eingabe {
  const { start, ende } = grenzen(e);
  if (start !== ende) {
    return { wert: e.wert.slice(0, start) + e.wert.slice(ende), start, ende: start };
  }
  if (start === 0) {
    return { wert: e.wert, start: 0, ende: 0 };
  }
  // Ein Zeichen vor dem Cursor – Ersatzpaare (z. B. Emoji aus der Zwischenablage) ganz
  const vorher = e.wert.charCodeAt(start - 1);
  const breite =
    start >= 2 && vorher >= 0xdc00 && vorher <= 0xdfff && e.wert.charCodeAt(start - 2) >= 0xd800
      ? 2
      : 1;
  const pos = start - breite;
  return { wert: e.wert.slice(0, pos) + e.wert.slice(start), start: pos, ende: pos };
}

/** „Leeren“: ganzes Feld leeren. */
export function feldLeeren(): Eingabe {
  return { wert: '', start: 0, ende: 0 };
}

/**
 * R6 „Warnung verdichten“: bei offener Tastatur stehen alle Warnungen in einer Zeile – die erste
 * (wichtigste, in der heutigen Anzeigereihenfolge) als Titel, dahinter die Anzahl der weiteren.
 * Leere Texte zählen nicht.
 */
export function verdichteWarnungen(
  warnungen: readonly string[],
): { titel: string; weitere: number; alle: string[] } | null {
  const alle = warnungen.map((w) => w.trim()).filter((w) => w !== '');
  const titel = alle[0];
  return titel === undefined ? null : { titel, weitere: alle.length - 1, alle };
}
